import * as React from "react";
import {
  Box,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { HelpTooltip } from "../help/HelpTooltip";
import { FlagIcon } from "../FlagIcon";

import {
  Controller,
  useFormContext,
  useWatch,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

type BaseProps = Omit<
  React.ComponentProps<typeof TextField>,
  "name" | "value" | "onChange" | "onBlur" | "ref"
>;

export interface PhoneInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends BaseProps {
  name: TName;
  label?: string;
  required?: boolean;
  tooltip?: string;
}

type CountryDial = {
  code: string;
  dial: string;
  name: string;
};

const DIALS: CountryDial[] = [
  // Priority countries
  { code: "PT", dial: "+351", name: "Portugal" },
  { code: "GB", dial: "+44", name: "United Kingdom" },
  { code: "IT", dial: "+39", name: "Italy" },
  { code: "FR", dial: "+33", name: "France" },
  { code: "ES", dial: "+34", name: "Spain" },

  // Others (alphabetical by country name)
  { code: "AT", dial: "+43", name: "Austria" },
  { code: "BE", dial: "+32", name: "Belgium" },
  { code: "BG", dial: "+359", name: "Bulgaria" },
  { code: "HR", dial: "+385", name: "Croatia" },
  { code: "CY", dial: "+357", name: "Cyprus" },
  { code: "CZ", dial: "+420", name: "Czech Republic" },
  { code: "DK", dial: "+45", name: "Denmark" },
  { code: "EE", dial: "+372", name: "Estonia" },
  { code: "FI", dial: "+358", name: "Finland" },
  { code: "DE", dial: "+49", name: "Germany" },
  { code: "GR", dial: "+30", name: "Greece" },
  { code: "HU", dial: "+36", name: "Hungary" },
  { code: "IE", dial: "+353", name: "Ireland" },
  { code: "LV", dial: "+371", name: "Latvia" },
  { code: "LT", dial: "+370", name: "Lithuania" },
  { code: "LU", dial: "+352", name: "Luxembourg" },
  { code: "MT", dial: "+356", name: "Malta" },
  { code: "NL", dial: "+31", name: "Netherlands" },
  { code: "PL", dial: "+48", name: "Poland" },
  { code: "RO", dial: "+40", name: "Romania" },
  { code: "SK", dial: "+421", name: "Slovakia" },
  { code: "SI", dial: "+386", name: "Slovenia" },
  { code: "SE", dial: "+46", name: "Sweden" },
];

function detectDial(value: string): CountryDial {
  const found = DIALS.find((d) => value.startsWith(d.dial));
  return found || DIALS.find((d) => d.code === "PT")!;
}

export function PhoneInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  label = "Phone number",
  required,
  tooltip,
  ...rest
}: PhoneInputProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const watchedValue = useWatch({ name, control }) as string | undefined;
  const { t } = useTranslation();

  const initial = React.useMemo(
    () => detectDial(watchedValue || ""),
    [watchedValue]
  );

  const [dial, setDial] = React.useState(initial.dial);
  const [localNumber, setLocalNumber] = React.useState(
    (watchedValue || "").replace(initial.dial, "")
  );

  React.useEffect(() => {
    const current = detectDial(watchedValue || "");
    setDial(current.dial);
    setLocalNumber((watchedValue || "").replace(current.dial, ""));
  }, [watchedValue]);

  const translateError = (message?: string) =>
    message ? t(message, { defaultValue: message }) : "";

  return (
    <FormControl fullWidth>
      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "#1A1A1A",
          }}
        >
          {label}
        </Typography>

        {required && (
          <Typography sx={{ color: "#E63946", fontWeight: 700 }}>*</Typography>
        )}

        {tooltip && (
          <HelpTooltip
            title={tooltip}
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={2000}
          >
            <Box
              role="button"
              tabIndex={0}
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: "#d7e2eb",
                color: "#1A1A1A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                cursor: "help",
                fontWeight: 700,
                userSelect: "none",
              }}
            >
              ?
            </Box>
          </HelpTooltip>
        )}
      </Box>

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const handleDialChange = (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            const ndial = e.target.value;
            setDial(ndial);
            field.onChange(`${ndial}${localNumber}`);
          };

          const handleLocalChange = (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            const raw = e.target.value.replace(/\D/g, "");
            setLocalNumber(raw);
            field.onChange(`${dial}${raw}`);
          };

          return (
            <>
              <TextField
                {...rest}
                fullWidth
                value={localNumber}
                onChange={handleLocalChange}
                onBlur={field.onBlur}
                error={!!fieldState.error}
                variant="outlined"
                type="tel"
                inputMode="numeric"
                inputProps={{
                  pattern: "[0-9]*",
                  ...(rest.inputProps || {}),
                }}
                sx={{
                  backgroundColor: "#ffffff !important",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    height: 46,
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    paddingLeft: 0,
                    "& fieldset": { borderColor: "#d8e0e8" },
                    "&:hover fieldset": { borderColor: "#b9c4d1" },
                    "&.Mui-focused fieldset": { borderColor: "#4b93ff" },
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#1A1A1A !important",
                    fontSize: { xs: 16, md: "0.92rem" },
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "#A0AEC0",
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pl: 1.2,
                        pr: 1,
                        height: "100%",
                      }}
                    >
                      <TextField
                        select
                        value={dial}
                        onChange={handleDialChange}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          minWidth: 95,
                          maxWidth: 120,
                          "& .MuiSelect-select": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            color: "#1A1A1A",
                            paddingY: 0,
                            paddingX: 0,
                            fontSize: { xs: 16, md: "0.9rem" },
                          },
                        }}
                        SelectProps={{
                          renderValue: (value) => {
                            const dialCode = value as string;
                            const selected = DIALS.find((d) => d.dial === dialCode);
                            return (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {selected && (
                                  <FlagIcon
                                    countryCode={selected.code}
                                    label={selected.name}
                                    sx={{ width: "20px", fontSize: "16px" }}
                                  />
                                )}
                                <span>{dialCode}</span>
                              </Box>
                            );
                          },
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                backgroundColor: "#ffffff !important",
                                color: "#1A1A1A",
                                maxHeight: 400,
                              },
                            },
                          },
                        }}
                      >
                        {DIALS.map((d) => (
                          <MenuItem
                            key={d.code}
                            value={d.dial}
                            sx={{
                              color: "#1A1A1A",
                              fontSize: { xs: 16, md: "0.9rem" },
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                              <FlagIcon
                                countryCode={d.code}
                                label={d.name}
                                sx={{ width: "24px", fontSize: "18px" }}
                              />
                              <span style={{ fontWeight: 500 }}>{d.dial}</span>
                              <span style={{ color: "#718096", fontSize: "0.9em" }}>{d.name}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>

                      <Box
                        sx={{
                          width: 2,
                          height: 22,
                          bgcolor: "#D9E2EC",
                          mx: 1.2,
                        }}
                      />
                    </Box>
                  ),
                }}
              />

              {fieldState.error && (
                <FormHelperText error sx={{ mt: 0.6 }}>
                  {translateError(fieldState.error.message)}
                </FormHelperText>
              )}
            </>
          );
        }}
      />
    </FormControl>
  );
}
